/**
 * Add a garment the catalogue has never seen. R-298.
 *
 * Every product here arrived through the Shopify export, which was right for
 * 380 styles on day one and left no way to add the 381st. The catalogue lives
 * in THIS system: Shopify is one channel a product can be published to, and a
 * screen that can only mirror Shopify is right for a mirror and wrong here.
 *
 * It writes the style AND one variant per colour and size, because a product
 * with no variants is a style nobody can order, stock or publish. Six colours
 * in five sizes is thirty rows, which is why they are generated.
 *
 * It publishes nothing. Putting it in a shop is a separate act on the style
 * page, where somebody sets a retail price against the cost.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Button, Callout, Field, inputClass, textareaClass } from '@/components/ui';
import { CategoryField } from '@/components/admin/category-field';
import { styleOptionsQuery } from '@/queries';

/** The five shapes the mockup can draw, and `other` for everything else. */
const TYPES: Array<{ key: string; label: string }> = [
  { key: 'tee', label: 'Tee' },
  { key: 'polo', label: 'Polo' },
  { key: 'hoodie', label: 'Hoodie' },
  { key: 'cap', label: 'Cap' },
  { key: 'tote', label: 'Tote' },
  { key: 'other', label: 'Something else' },
];

interface Made { id: string; name: string; variants: number }

export function NewProduct() {
  const [open, setOpen] = useState(false);
  if (!open) return <Button onClick={() => setOpen(true)}>New product</Button>;
  return (
    <div className="w-full">
      <Form onClose={() => setOpen(false)} />
    </div>
  );
}

function Form({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [type, setType] = useState('tee');
  const [price, setPrice] = useState('');
  // Spec 014, FR-1451. No sizes to start with. "XS, S, M, L, XL" was a guess
  // that nobody asked for, and a style in 6, 8, 10 got five wrong stock items
  // when it was not cleared. The catalogue's own sizes are offered below.
  const [sizes, setSizes] = useState('');
  const options = useQuery({ ...styleOptionsQuery, retry: false }).data;
  const offered = (options?.sizes ?? []).slice(0, 16).map((s) => s.name);
  const [colours, setColours] = useState([{ name: 'Black', hex: '#16191C' }]);
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [made, setMade] = useState<Made | null>(null);

  const sizeList = [...new Set(sizes.split(',').map((s) => s.trim()).filter(Boolean))];
  const colourList = colours.filter((c) => c.name.trim() !== '');
  const variantCount = sizeList.length * colourList.length;

  const create = useMutation({
    mutationFn: async () => {
      const res = await fetch('/api/products', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          category: category.trim(),
          type,
          // Dollars on the screen, cents on the wire. Everything about money in
          // this system is an integer number of cents.
          base_price_cents: Math.round(Number(price) * 100),
          colors: colourList.map((c) => ({ name: c.name.trim(), hex: c.hex })),
          sizes: sizeList,
          description: description.trim() || null,
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as
          | { error?: { message?: string } } | null;
        throw new Error(body?.error?.message ?? `Request failed (${res.status})`);
      }
      return (await res.json()) as Made;
    },
    onSuccess: async (result) => {
      setError(null);
      setMade(result);
      await qc.invalidateQueries({ queryKey: ['admin', 'product-listing'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  if (made) {
    return (
      <div className="rounded-[10px] border border-line bg-panel p-5">
        <Callout tone="ok" title={`${made.name} is in the catalogue`}>
          {made.variants} {made.variants === 1 ? 'variant' : 'variants'}, one for
          every colour and size, each with its own stock item. Book it in on a
          purchase order to put it on a shelf. It is not in any shop yet.
        </Callout>
        <div className="mt-4 flex gap-3">
          <Button onClick={() => { setMade(null); setName(''); setCategory(''); setPrice(''); }}>
            Add another
          </Button>
          <Button weight="secondary" onClick={onClose}>Done</Button>
        </div>
      </div>
    );
  }

  function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!name.trim()) return setError('Give the garment a name.');
    if (!category.trim()) return setError('A product belongs to a category. It is how a shopper finds it.');
    if (!(Number(price) > 0)) {
      return setError('Give the blank cost. A garment priced at nothing makes every quote using it look free.');
    }
    if (colourList.length === 0) return setError('Name at least one colour.');
    if (sizeList.length === 0) return setError('Name at least one size.');
    setError(null);
    create.mutate();
  }

  return (
    <form
      onSubmit={submit}
      className="rounded-[10px] border border-line bg-panel p-5"
      aria-label="Add a garment"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-[15px] font-semibold">Add a garment</h2>
          <p className="mt-1 text-[12.5px] text-muted">
            A style this catalogue has never seen. It gets one variant and one
            stock item per colour and size, and goes into no shop by itself.
          </p>
        </div>
        <Button type="button" weight="secondary" onClick={onClose}>Cancel</Button>
      </div>

      {error && <div className="mt-4"><Callout tone="danger" title={error} /></div>}

      <div className="mt-5 grid gap-5 sm:grid-cols-2">
        <Field label="Name" htmlFor="np-name" hint="As the studio says it, in full.">
          <input
            id="np-name" className={inputClass} value={name}
            onChange={(e) => setName(e.target.value)} autoComplete="off"
          />
        </Field>

        {/* Pick one the catalogue already uses, or add a new one. There is no
            list of categories in this code: Guy keeps it himself. Tracker #12. */}
        <Field label="Category" htmlFor="np-category" hint="Pick one, or add a new one.">
          <CategoryField id="np-category" value={category} onChange={setCategory} />
        </Field>

        <Field label="Shape" htmlFor="np-type" hint="What the mockup draws.">
          <select
            id="np-type" className={inputClass} value={type}
            onChange={(e) => setType(e.target.value)}
          >
            {TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
        </Field>

        <Field
          label="Blank cost" htmlFor="np-price"
          hint="What the garment costs before decoration, in dollars."
        >
          <input
            id="np-price" className={inputClass} inputMode="decimal" value={price}
            onChange={(e) => setPrice(e.target.value.replace(/[^0-9.]/g, ''))}
            placeholder="19.95"
          />
        </Field>

        <div className="sm:col-span-2">
          <Field
            label="Sizes" htmlFor="np-sizes"
            hint="Separated by commas, in the order they should be offered."
          >
            <input
              id="np-sizes" className={inputClass} value={sizes} placeholder="For example 8, 10, 12"
              onChange={(e) => setSizes(e.target.value)}
            />
          </Field>
          {offered.length > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-1.5" aria-label="Sizes the catalogue uses">
              <span className="text-[12px] text-muted">Add a size the catalogue uses:</span>
              {offered.filter((s) => !sizeList.some((x) => x.toLowerCase() === s.toLowerCase())).map((s) => (
                <button
                  key={s}
                  type="button"
                  className="rounded-[6px] border border-line px-2 py-0.5 text-[12px] hover:bg-raised"
                  onClick={() => setSizes((now) => (now.trim() ? `${now.trim().replace(/,\s*$/, '')}, ${s}` : s))}
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="sm:col-span-2">
          <fieldset>
            <legend className="text-sm font-semibold">Colours</legend>
            <p className="mt-1 text-[13px] text-muted">
              A name and a swatch. The swatch is what a shopper picks from, so a
              colour without one draws as grey.
            </p>
            <div className="mt-2 space-y-2">
              {colours.map((c, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    aria-label={`Colour ${i + 1} name`}
                    className={`${inputClass} max-w-[240px]`}
                    value={c.name}
                    onChange={(e) => setColours(colours.map((x, j) =>
                      j === i ? { ...x, name: e.target.value } : x))}
                  />
                  <input
                    aria-label={`Colour ${i + 1} swatch`}
                    type="color"
                    className="h-8 w-12 rounded-[6px] border border-line-strong bg-panel"
                    value={c.hex}
                    onChange={(e) => setColours(colours.map((x, j) =>
                      j === i ? { ...x, hex: e.target.value } : x))}
                  />
                  {colours.length > 1 && (
                    <Button
                      type="button" weight="tertiary"
                      onClick={() => setColours(colours.filter((_, j) => j !== i))}
                    >
                      Remove
                    </Button>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-2">
              <Button
                type="button" weight="secondary"
                onClick={() => setColours([...colours, { name: '', hex: '#111111' }])}
              >
                Add a colour
              </Button>
            </div>
          </fieldset>
        </div>

        <div className="sm:col-span-2">
          <Field label="Description" htmlFor="np-description" optional
            hint="What a shopper reads. A product with none shows a grey box and a name."
          >
            <textarea
              id="np-description" className={textareaClass} value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Field>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <Button type="submit" disabled={create.isPending}>
          {create.isPending ? 'Adding...' : 'Add the garment'}
        </Button>
        <span className="text-[13px] text-muted">
          {variantCount > 0
            ? `${variantCount} ${variantCount === 1 ? 'variant' : 'variants'}, one for every colour and size.`
            : 'Name a colour and a size and the variants are made with it.'}
        </span>
      </div>
    </form>
  );
}
